// Phase 5.9 · 소비자 마이페이지 [내 쿠폰]
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Ticket, CheckCircle2, Clock, XCircle } from 'lucide-react';
import api from '@/lib/api';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const kindLabel = (k, v, maxd) => {
  if (k === 'free_ship') return '무료배송';
  if (k === 'percent') return maxd ? `${v}% 할인 (최대 ${won(maxd)})` : `${v}% 할인`;
  return `${won(v)} 할인`;
};

const statusMeta = {
  available: { label: '사용 가능', icon: CheckCircle2, cls: 'bg-emerald-500/10 text-emerald-700' },
  used:      { label: '사용 완료', icon: XCircle,      cls: 'bg-gray-500/10 text-gray-500' },
  expired:   { label: '기간 만료', icon: Clock,        cls: 'bg-orange-500/10 text-orange-700' },
};

export default function MyCoupons() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('available');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/my/coupons')
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((uc) => uc.status === tab);
  const counts = {
    available: items.filter((x) => x.status === 'available').length,
    used:      items.filter((x) => x.status === 'used').length,
    expired:   items.filter((x) => x.status === 'expired').length,
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="my-coupons-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black">
        <ArrowLeft size={14}/> 뒤로
      </button>
      <h1 className="text-2xl font-black inline-flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <Ticket size={22}/> 내 쿠폰
      </h1>
      <div className="inline-flex rounded-full border border-black/10 dark:border-white/10 overflow-hidden">
        {['available', 'used', 'expired'].map((k) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-bold ${tab === k ? 'bg-[#FF3B30] text-white' : ''}`} data-testid={`my-coupons-tab-${k}`}>
            {statusMeta[k].label} ({counts[k]})
          </button>
        ))}
      </div>
      {loading ? (
        <p className="text-center text-sm text-gray-500 py-8" data-testid="my-coupons-loading">불러오는 중…</p>
      ) : filtered.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-16" data-testid="my-coupons-empty">
          {statusMeta[tab].label} 쿠폰이 없습니다.
        </p>
      ) : (
        <div className="space-y-3" data-testid="my-coupons-list">
          {filtered.map((uc) => {
            const c = uc.coupon || {};
            const M = statusMeta[uc.status] || statusMeta.available;
            return (
              <div key={uc.user_coupon_id}
                className="rounded-xl border border-black/10 dark:border-white/10 p-4 flex items-center gap-3 hover:shadow-sm transition-shadow"
                data-testid={`my-coupon-${uc.user_coupon_id}`}>
                <div className="w-14 h-14 rounded-lg bg-[#FF3B30]/10 text-[#FF3B30] inline-flex items-center justify-center shrink-0">
                  <Ticket size={28}/>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black truncate">{c.name || '(쿠폰명 없음)'}</p>
                  <p className="text-xs text-[#FF3B30] font-black mt-0.5">{kindLabel(c.kind, c.discount_value, c.max_discount)}</p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {c.min_order_amount > 0 && `최소 ${won(c.min_order_amount)} 이상 · `}
                    {c.valid_to ? `${format(new Date(c.valid_to), 'yyyy.M.d까지', { locale: ko })}` : '기한 무제한'}
                    {c.issuer === 'admin' && <span className="ml-1 px-1 rounded bg-black/70 text-white text-[9px]">LIVEMOA</span>}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black inline-flex items-center gap-1 shrink-0 ${M.cls}`}>
                  <M.icon size={10}/> {M.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
